import { useState, useEffect, useMemo } from 'react'
import styled from '@emotion/styled'
import FolderIcon from '@mui/icons-material/Folder'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import FilledStar from '@mui/icons-material/Star'
import BorderStar from '@mui/icons-material/StarBorder'
import StorageIcon from '@mui/icons-material/Storage'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { object, string, array, bool, oneOfType } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { Paper } from '@components'
import { colors } from '@global/constants/colors'
import { useAppSelector } from '@redux/hooks'
import { getCellContent } from 'functions'
import { CvsTable } from '../../components/table/cvs-table'
import * as SubmissionActions from '../submissions.actions'
import { BulkUploadModal } from './bulk-upload-modal'
import { t } from '@lingui/core/macro'

const TableCellEllipsis = styled(TableCell)`
  whitespace: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TemplateList = ({ rows = [], columns = [], noAdd = false, keyField = 'id', ...rest }) => {
  const dispatch = useDispatch()
  const [DATASETS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )

  const [redirectPath, setRedirectPath] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [bulkUploadModalIsOpen, setBulkUploadModalIsOpen] = useState(false)
  const ROW_LEVEL_PERMISSIONS_ENABLED = useSelector(
    state => state.Auth.features?.['row-level-permissions'],
  )

  const useBulkUpload = useSelector(state => state.Auth.features['show-bulk-upload-menu'])
  const showColumns = !useBulkUpload ? columns.filter(c => c.lookup) : columns

  const [displayRows, setDisplayRows] = useState(rows)

  const permissions = useSelector(state => state.Auth.permissions)
  const hasAllPrivileges = useMemo(() => {
    return ['all_privileges'].some(permission => {
      return Object.keys(permissions)
        .filter(permissionKey => permissions[permissionKey])
        .includes(permission)
    })
  }, [permissions])

  useEffect(() => {
    setDisplayRows(rows)
  }, [rows])

  const rollup = useSelector(state => state.Auth.features.rollup)

  function navigateToCreateSubmission(row) {
    setRedirectPath(`/codes/create/${row.id}`)
  }

  const handleRowClick = row => {
    if (rollup) {
      navigateToCreateSubmission(row)
    } else {
      setRedirectPath(`/codes/create/${row.id}`)
    }
  }

  const toggleFavorite = (row, index, event) => {
    event.stopPropagation()
    dispatch(SubmissionActions.toggleFavorite(row))
    const newRows = [...displayRows]
    const replaceIndex = displayRows.findIndex(r => r.id === row.id)
    newRows[replaceIndex] = { ...row, favorite: !row.favorite }
    setDisplayRows(newRows)
  }

  const canViewTemplateDetails = rollup && (hasAllPrivileges || ROW_LEVEL_PERMISSIONS_ENABLED)

  return (
    <>
      {!redirectPath ? (
        <>
          <CvsTable
            {...rest}
            rows={displayRows}
            columns={showColumns}
            defaultSort="date"
            sortOrder="desc"
            noAdd={noAdd}
            noPagination={true}
            style={{ maxWidth: '100%' }}
            headerRowPadding={50}
          >
            {({ row, index }) => {
              return (
                <TableRow
                  hover
                  onClick={() => handleRowClick(row)}
                  tabIndex={-1}
                  key={row[keyField]}
                  style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                >
                  <TableCellEllipsis
                    component="th"
                    scope="row"
                    padding="default"
                    style={{ paddingLeft: 50 }}
                  >
                    {row.name}
                  </TableCellEllipsis>
                  <TableCellEllipsis component="th" scope="row" padding="default">
                    {getCellContent('date', row.updated_at)}
                  </TableCellEllipsis>
                  <TableCellEllipsis component="th" scope="row" padding="default">
                    {row.description}
                  </TableCellEllipsis>
                  <TableCellEllipsis
                    component="th"
                    scope="row"
                    padding="default"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    onClick={e => {
                      e.stopPropagation()
                      setBulkUploadModalIsOpen(true)
                      setSelectedTemplate(row)
                    }}
                  >
                    <div style={{ display: 'flex' }}>
                      {useBulkUpload && (
                        <Tooltip title={t`Import file`}>
                          <ImportExportIcon
                            data-test-id={row.id}
                            style={{ color: colors.gray.dark, marginRight: 8 }}
                          />
                        </Tooltip>
                      )}

                      {canViewTemplateDetails && (
                        <div
                          style={{ display: 'inline-block', marginRight: 8 }}
                          onClick={e => {
                            e.stopPropagation()
                            setRedirectPath(`/template/${row.id}/data`)
                          }}
                        >
                          <Tooltip title={t`View dataset`}>
                            <StorageIcon style={{ color: colors.gray.medium }} />
                          </Tooltip>
                        </div>
                      )}
                      <div style={{ marginRight: 8, display: 'inline-block' }}>
                        {row.favorite ? (
                          <div style={{ color: 'rgb(245, 171, 53)' }}>
                            <Tooltip
                              title={
                                DATASETS_ENABLED ? t`Unfavorite Dataset` : t`Unfavorite Template`
                              }
                            >
                              <FilledStar
                                onClick={e => {
                                  toggleFavorite(row, index, e)
                                }}
                              />
                            </Tooltip>
                          </div>
                        ) : (
                          <Tooltip
                            title={DATASETS_ENABLED ? t`Favorite Dataset` : t`Favorite Template`}
                          >
                            <BorderStar
                              onClick={e => {
                                toggleFavorite(row, index, e)
                              }}
                              style={{ color: colors.gray.dark }}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </TableCellEllipsis>
                </TableRow>
              )
            }}
          </CvsTable>
          {!!selectedTemplate && (
            <BulkUploadModal
              open={bulkUploadModalIsOpen}
              setOpen={setBulkUploadModalIsOpen}
              templateId={selectedTemplate.id}
              requireApproval={Boolean(selectedTemplate.settings?.require_approval)}
            />
          )}
        </>
      ) : (
        <Redirect to={redirectPath} push={true} />
      )}
    </>
  )
}

TemplateList.propTypes = {
  rows: array,
  columns: array,
  noAdd: bool,
  keyField: string,
}

export const FolderCard = ({ templates, folderName }) => {
  // const showActions = useSelector(state => state.Auth.features['show-bulk-upload-menu'])
  return (
    <Paper>
      <Typography
        variant="h6"
        style={{
          paddingTop: 24,
          paddingLeft: 50,
          paddingRight: 24,
          paddingBottom: 24,
        }}
      >
        <FolderIcon style={{ marginRight: 10 }} />
        {folderName}
      </Typography>
      <TemplateList
        rows={templates}
        keyField="id"
        orderBy="updated_at"
        columns={[
          { label: t`Name`, width: '25%', lookup: 'name' },
          { label: t`Last Updated`, width: '25%', lookup: 'updated_at' },
          { label: t`Description`, width: '25%', lookup: 'description' },
          { label: t`Actions`, width: '25%', lookup: 'favorite' },
        ]}
      />
    </Paper>
  )
}

FolderCard.propTypes = {
  templates: oneOfType([object, array]).isRequired,
  folderName: string.isRequired,
}
